/* Collapsible JSON tree — used by the Run inspector for the context and
 * other already-stored values. Read-only by design.
 * Values under sensitive keys (password/secret/token/credential) are
 * MASKED by default — click to reveal — so a screen-share of the studio
 * never leaks credentials. */
import React, {
    useState
} from 'react';

const SENSITIVE_KEY = /password|secret|token|credential/i;

function SecretLeaf({
    value
}: {
    value: string;
}) {
    const [show, setShow] = useState(false);
    if (show) {
        return (
            <button className="aps-tree-secret revealed"
                onClick={() => setShow(false)}
                title="Click to mask again">
                &quot;{value}&quot;
            </button>
        );
    }
    return (
        <button className="aps-tree-secret" onClick={() => setShow(true)}
            title="Sensitive value — click to reveal">
            ••••••••
        </button>
    );
}

function Leaf({
    value
}: {
    value: any;
}) {
    if (value === null || value === undefined) {
        return <span className="aps-tree-val-null">null</span>;
    }
    if (typeof value === 'string') {
        return <span className="aps-tree-val-string">&quot;{value}&quot;</span>;
    }
    if (typeof value === 'number') {
        return <span className="aps-tree-val-num">{String(value)}</span>;
    }
    if (typeof value === 'boolean') {
        return <span className="aps-tree-val-num">{String(value)}</span>;
    }
    return <span>{String(value)}</span>;
}

function Node({
    name,
    value,
    depth
}: {
    name: string | null;
    value: any;
    depth: number;
}) {
    const isObj = value !== null && typeof value === 'object';
    const [open, setOpen] = useState(depth < 2);
    if (!isObj) {
        const sensitive = name != null && SENSITIVE_KEY.test(name)
            && typeof value === 'string' && value.length > 0;
        return (
            <div>
                {name != null && <span className="aps-tree-key">{name}</span>}
                {name != null && ': '}
                {sensitive
                    ? <SecretLeaf value={value as string} />
                    : <Leaf value={value} />}
            </div>
        );
    }
    const isArr = Array.isArray(value);
    const entries: Array<[string, any]> = isArr
        ? value.map((v: any, i: number) => [String(i), v] as [string, any])
        : Object.entries(value);
    const preview = isArr ? `[${entries.length}]` : `{${entries.length}}`;
    return (
        <div>
            <button className="aps-tree-toggle" onClick={() => setOpen(!open)}
                aria-expanded={open}>
                {open ? '▾' : '▸'}
            </button>
            {name != null && <span className="aps-tree-key">{name}</span>}
            {name != null && ': '}
            {!open && <span className="aps-tree-val-null">{preview}</span>}
            {open && (
                <div className="aps-tree-children">
                    {entries.length === 0 && (
                        <span className="aps-tree-val-null">{isArr ? '(empty array)' : '(empty object)'}</span>
                    )}
                    {entries.map(([k, v]) => (
                        <Node key={k} name={k} value={v} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function JsonTree({
    value
}: {
    value: any;
}) {
    return (
        <div className="aps-tree">
            <Node name={null} value={value} depth={0} />
        </div>
    );
}
